/** AudioStreamPlayer2D strict validators. */
// The base chain. Registration happens on import, so a test that loads only this slice resolves
// an inherited key only when the ancestor is imported too.
import '../../base/node2d/linterParser.js';
import { validatorRegistry } from '../../../linter/ValidatorRegistry.js';
import { layerBitmask, v } from '../../../linter/validators/index.js';
import { busValidator } from '../busValidator.js';
import { AUDIO_SERVER_PLAYBACK_TYPE_NAMES } from '../../../godot/audio/audioServer.js';
validatorRegistry.registerAll('AudioStreamPlayer2D', {
    // audio_stream_player_2d.cpp:429 hints PROPERTY_HINT_RESOURCE_TYPE
    // "AudioStream" (a type constraint, not a range). set_stream (:201-203)
    // delegates to AudioStreamPlayerInternal::set_stream
    // (audio_stream_player_internal.cpp:254-263), a bare assignment: format-only.
    stream: v.resourceReference('stream'),
    // audio_stream_player_2d.cpp:430, PROPERTY_HINT_RANGE "-80,24,suffix:dB":
    // both ends closed, and set_volume_db assigns straight through, so out of
    // range warns. Its one refusal is ERR_FAIL_COND_MSG(Math::is_nan(p_volume),
    // ...): `nan` is dropped, `inf` and `-inf` are stored unaltered.
    volume_db: v.float('volume_db', {
        min: -80,
        max: 24,
        hinted: 'audio_stream_player_2d.cpp:430',
        nan: 'audio_stream_player_2d.cpp:210',
    }),
    // audio_stream_player_internal.cpp:314, ERR_FAIL_COND(p_pitch_scale <= 0.0),
    // against a hint (audio_stream_player_2d.cpp:432) of
    // "0.01,4,0.01,or_greater", where `or_greater` opens the ceiling. The two floors
    // sit apart, so (0, 0.01) loads into Godot and only warns.
    pitch_scale: v.positiveFloat('pitch_scale', "Property 'pitch_scale' must be greater than 0. Zero or negative pitch breaks audio playback.", {
        min: 0.01,
        enforced: 'audio_stream_player_internal.cpp:314',
        hinted: 'audio_stream_player_2d.cpp:432',
    }),
    playing: v.boolean('playing'),
    autoplay: v.boolean('autoplay'),
    stream_paused: v.boolean('stream_paused'),
    // audio_stream_player_2d.cpp:300, ERR_FAIL_COND(p_pixels <= 0.0), against a
    // hint (:436) of "1,4096,1,or_greater,exp,suffix:px": `or_greater` opens
    // the ceiling and the hint's floor of 1 sits above the setter's, so
    // (0, 1) loads into Godot and only warns.
    max_distance: v.positiveFloat('max_distance', undefined, {
        min: 1,
        enforced: 'audio_stream_player_2d.cpp:300',
        hinted: 'audio_stream_player_2d.cpp:436',
    }),
    // audio_stream_player_2d.cpp:437 is PROPERTY_HINT_EXP_EASING (no range) and
    // set_attenuation (:308) is a bare assignment, so there is no bound to check.
    attenuation: v.float('attenuation'),
    // audio_stream_player_2d.cpp:349, ERR_FAIL_COND_MSG(p_panning_strength < 0, ...)
    // enforces the floor only. The hint (:439) is "0,3,0.01,or_greater": or_greater
    // opens the ceiling, so there is no upper bound to check.
    panning_strength: v.float('panning_strength', {
        min: 0,
        enforced: { min: 'audio_stream_player_2d.cpp:349' },
    }),
    // Bare uint32_t assignment (:316): the parameter type is the only ceiling, so
    // the citation is the setter signature rather than an ERR_FAIL.
    area_mask: layerBitmask('area_mask', {
        hinted: 'audio_stream_player_2d.cpp:441',
        width: 'uint32' /* audio_stream_player_2d.h:124 */,
    }),
    // audio_stream_player_internal.cpp:337 is a bare assignment; no engine-side range check.
    playback_type: v.enumInt('playback_type', AUDIO_SERVER_PLAYBACK_TYPE_NAMES, {
        hinted: 'audio_stream_player_2d.cpp:442',
    }),
    bus: busValidator,
    // audio_stream_player_internal.cpp:323 drops the write when <= 0.
    max_polyphony: v.int('max_polyphony', {
        min: 1,
        message: "Property 'max_polyphony' must be at least 1. Values below 1 cause errors.",
        enforced: 'audio_stream_player_internal.cpp:323',
    }),
});
//# sourceMappingURL=linterParser.js.map